import React from "react";
import { motion } from "framer-motion";
import { Check, DotsThreeCircle } from "@phosphor-icons/react";
import { CF_INPUT, ContactStep } from "./ContactStep";
import { ConsentStep } from "./ConsentStep";

const PictureCards = ({ step, value, onPick }) => {
  const many = step.options.length > 4;
  return (
    <div className={`grid gap-3 ${many ? "grid-cols-2 sm:grid-cols-3" : "grid-cols-2"}`} data-testid={`case-quiz-cards-${step.key}`}>
      {step.options.map((o, idx) => {
        const active = value === o.value;
        return (
          <motion.button
            key={o.value}
            type="button"
            onClick={() => onPick(o.value)}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.04, duration: 0.3 }}
            whileHover={{ y: -4 }}
            whileTap={{ scale: 0.96 }}
            aria-pressed={active}
            className={`group relative flex flex-col overflow-hidden rounded-xl border bg-[#0d2236] text-left transition-colors duration-200 ${active ? "border-[var(--cf-accent)] ring-2 ring-[var(--cf-accent)]" : "border-white/10 hover:border-white/30"}`}
            data-testid={`case-quiz-card-option-${o.value}`}
          >
            <span className={`relative block w-full overflow-hidden ${many ? "aspect-[16/10]" : "aspect-[4/3]"}`}>
              {o.img ? (
                <img src={o.img} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
              ) : (
                <span className="grid h-full w-full place-items-center bg-white/5 text-[var(--cf-accent)]">
                  <DotsThreeCircle size={40} weight="duotone" />
                </span>
              )}
              {active && (
                <span className="absolute right-2 top-2 grid h-7 w-7 place-items-center rounded-full bg-[var(--cf-accent)] text-white shadow-lg">
                  <Check size={16} weight="bold" />
                </span>
              )}
            </span>
            <span className="block px-3 py-2.5 text-sm font-bold leading-tight text-white sm:text-[15px]">{o.label}</span>
          </motion.button>
        );
      })}
    </div>
  );
};

const ChoiceList = ({ step, value, onPick }) => (
  <div className="grid gap-2.5" data-testid={`case-quiz-choices-${step.key}`}>
    {step.options.map((o, idx) => {
      const active = value === o.value;
      return (
        <motion.button
          key={String(o.value)}
          type="button"
          onClick={() => onPick(o.value)}
          initial={{ opacity: 0, x: 12 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: idx * 0.05 }}
          whileTap={{ scale: 0.98 }}
          aria-pressed={active}
          className={`flex min-h-[56px] items-center gap-3 rounded-xl border px-4 text-left text-base font-semibold transition-colors duration-200 ${active ? "cf-soft border-[var(--cf-accent)] text-white" : "border-white/15 bg-white/[0.04] text-slate-100 hover:border-white/30 hover:bg-white/[0.08]"}`}
          data-testid={`case-quiz-choice-${step.key}-${String(o.value)}`}
        >
          <span className={`grid h-7 w-7 shrink-0 place-items-center rounded-md border text-xs font-bold ${active ? "border-transparent bg-[var(--cf-accent)] text-white" : "border-white/20 text-slate-400"}`}>
            {active ? <Check size={14} weight="bold" /> : String.fromCharCode(65 + idx)}
          </span>
          {o.label}
        </motion.button>
      );
    })}
  </div>
);

export const CaseStepBody = ({ step, answers, set, onPick, consentProps }) => {
  if (step.kind === "cards") return <PictureCards step={step} value={answers[step.field]} onPick={onPick} />;
  if (step.kind === "choices") return <ChoiceList step={step} value={answers[step.field]} onPick={onPick} />;
  if (step.kind === "contact") return <ContactStep a={answers} set={set} zipOptional={step.zipOptional} />;
  if (step.kind === "consent") return <ConsentStep a={answers} set={set} {...consentProps} />;
  return (
    <textarea
      rows={4}
      maxLength={1500}
      value={answers[step.field]}
      onChange={(e) => set(step.field, e.target.value)}
      placeholder="What happened, where, and how you're feeling now…"
      className={`${CF_INPUT} !h-auto resize-none py-3`}
      data-testid={`case-quiz-input-${step.key}`}
    />
  );
};
